/**
 * HoldingsPanel 헬퍼 함수 및 컴포넌트
 */
import React from 'react';
import type { PortfolioResponse } from '../types/position';
import type { AIStockData } from '../types/watchlist';
import { strategyApi } from '../services/strategyApi';
import { mapStrategySignalResponse } from '../utils/strategySignals';
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';

// 손익률에 따른 리스크 레벨 계산
export const deriveRiskLevel = (pnlPercent: number): number => {
  if (pnlPercent >= 5) return 3;
  if (pnlPercent >= 0) return 4;
  if (pnlPercent > -3) return 5;
  if (pnlPercent > -7) return 7;
  if (pnlPercent > -12) return 8;
  return 9;
};

// 신호 강도를 신뢰도로 변환
export const mapConfidence = (strength: number | undefined): number => {
  if (typeof strength !== 'number' || Number.isNaN(strength)) return 0;
  return Math.max(0, Math.min(10, Math.round(strength * 10)));
};

// 계좌 정보 타입
export interface AccountInfo {
  portfolio_value: number;
  initial_deposit?: number;
  total_return_percent?: number;
}

// 보유 종목 및 계좌 정보 fetch
export const fetchHoldingsData = async (
  isMounted: () => boolean,
  setHoldings: (h: AIStockData[]) => void,
  setAccountInfo: (a: AccountInfo | null) => void,
  setError: (e: string | null) => void,
  setLoading: (l: boolean) => void
): Promise<void> => {
  const portfolioResponse = await apiFetch(`${API_BASE_URL}/api/portfolio`);
  if (!portfolioResponse.ok) {
    throw new Error(`포트폴리오 API 오류: ${portfolioResponse.status}`);
  }

  const portfolioData: PortfolioResponse = await portfolioResponse.json();

  if (isMounted()) {
    setAccountInfo({
      portfolio_value: portfolioData.account.portfolio_value,
      initial_deposit: portfolioData.account.initial_deposit,
      total_return_percent: portfolioData.account.total_return_percent,
    });
  }

  if (!portfolioData.positions || portfolioData.positions.length === 0) {
    if (isMounted()) {
      setHoldings([]);
      setError(null);
      setLoading(false);
    }
    return;
  }

  const baseHoldings: AIStockData[] = portfolioData.positions.map((position) => ({
    ticker: position.symbol,
    sector: position.symbol.includes('USD') ? 'Crypto' : 'Stock',
    confidence: 0,
    risk_level: deriveRiskLevel(position.unrealized_pl_percent),
    catalyst: `보유: ${position.qty.toFixed(2)}주 | ${position.unrealized_pl_percent >= 0 ? '+' : ''}${position.unrealized_pl_percent.toFixed(2)}% | $${position.market_value.toFixed(2)}`,
    market_value: position.market_value,
    unrealized_pl_percent: position.unrealized_pl_percent,
    qty: position.qty,
    signal_status: 'loading',
  })) as AIStockData[];

  if (isMounted()) {
    setHoldings(baseHoldings);
    setError(null);
    setLoading(false);
  }

  // 신호 데이터로 enrichment
  const enriched = await Promise.all(
    baseHoldings.map(async (holding) => {
      try {
        const signal = await strategyApi.getSignal(holding.ticker);
        const signals = mapStrategySignalResponse(signal);
        return {
          ...holding,
          confidence: mapConfidence(signal.strength),
          strategy_signals: signals,
          catalyst: `${holding.catalyst} · 신호: ${signal.action}`,
          signal_status: 'ready' as const,
          signal_error: undefined,
        };
      } catch (error: any) {
        return {
          ...holding,
          signal_status: 'error' as const,
          signal_error: error.message || '신호 조회 실패',
        };
      }
    })
  );

  if (isMounted()) setHoldings(enriched);
};

// 계좌 요약 컴포넌트
interface AccountSummaryProps {
  accountInfo: AccountInfo;
}

export const AccountSummary: React.FC<AccountSummaryProps> = ({ accountInfo }) => {
  if (!accountInfo.initial_deposit || accountInfo.total_return_percent === undefined) return null;

  return (
    <div className="account-summary">
      <span className="account-label">총 자산:</span>
      <span className="account-value">${accountInfo.portfolio_value.toFixed(2)}</span>
      <span className="account-divider">|</span>
      <span className="account-label">총 수익률:</span>
      <span className={`account-return ${accountInfo.total_return_percent >= 0 ? 'positive' : 'negative'}`}>
        {accountInfo.total_return_percent >= 0 ? '+' : ''}{accountInfo.total_return_percent.toFixed(2)}%
      </span>
    </div>
  );
};
